import Vue from "vue";
import Component from "vue-class-component";
import { Prop, Watch } from "vue-property-decorator";
import { mapGetters } from "vuex";
import * as accouttypes from "@account/store/types";
import api from "../../api";
import WebpartHeader from "./WebpartHeader.vue";
import WebpartList from "./WebpartList.vue";
import WebpartModal from "../modals/webpart-modal/WebpartModal.vue";
import WebpartPreviewModal from "../modals/webpart-preview-modal/WebpartPreviewModal.vue";
import { LoadingBar } from "@appbase/components/loading";

const getInitialWebpart = (comCode: string): webpartv3.Webpart => {
    return {
        comCode: comCode,
        title: "",
        titleEn: "",
        titleCh: "",
        titleJp: "",
        component: "",
        enabled: true,
        isFixedSize: true,
        columns: 1,
        rows: 1,
        props: null,
        roles: [],
    };
};

/**
 * 웹파트를 추가/편집/삭제를 통해 관리하는 화면
 */
@Component({
    components: {
        WebpartHeader,
        WebpartList,
        WebpartModal,
        WebpartPreviewModal,
        LoadingBar,
    },
    computed: {
        ...mapGetters({
            current: accouttypes.CURRENT,
        }),
    },
})
export default class WebpartManagement extends Vue {
    /** 회사 코드 */
    @Prop({ type: String, required: true }) company!: string;

    /** 현재 로그인된 사용자의 애플리케이션 데이터 */
    current!: account.TCurrent;

    /** Webpart 목록 */
    webparts: webpartv3.Webpart[] = [];
    loading: boolean = false;

    //계열사 변경 감지
    @Watch("company")
    companyChanged() {
        this.refresh();
    }

    async created() {
        this.loading = true;
        await this.refresh();
        this.loading = false;
    }

    /**
     * 웹파트 목록을 새로고침합니다.
     */
    async refresh() {
        // 로그인된 사용자의 회사에서 만든 모든 웹파트 목록 조회
        this.webparts = await api.webpart.get(this.company);
    }

    /**
     * 웹파트 수정을 위한 모달 오픈
     */
    handleCreateEdit(webpart?: webpartv3.Webpart) {
        const modal: appbase.IModalOpenable = this.$refs["modal"] as any;
        const { company: comCode } = this;
        const options: appbase.TModalInfo<any> = {
            component: WebpartModal,
            props: {
                company: comCode,
                mode: webpart ? "edit" : "new",
                // 모달에서 정보가 변경될 것이기 때문에
                // webpart 참조를 넘기는 것보다 clone해서 전달
                webpart: { ...(webpart || getInitialWebpart(comCode)) },
                title: webpart ? this.$t("웹파트_수정") : this.$t("웹파트_생성").toString(),
            },
            events: {
                // 저장 버튼 클릭 이벤트 핸들러
                save: async (webpart: webpartv3.Webpart) => {
                    console.log("save", webpart);
                    const result = await api.webpart.save(webpart);
                    await (window as any).alertAsync(this.$t("저장되었습니다").toString());
                    await this.refresh();
                    modal.closeModal();
                },
                delete: async (webpart: webpartv3.Webpart) => {
                    if (await this.handleDelete(webpart)) {
                        // await this.refresh() <-- handleDelete 함수에서 새로고침함
                        modal.closeModal(); // 삭제 성공시 모달 닫음
                    }
                },
                // 취소 버튼 클릭 이벤트 핸들러
                cancel: (webpart: webpartv3.Webpart) => {
                    console.log("cancel", webpart);
                    modal.closeModal();
                },
            },
        };
        modal.openModal(options);
    }

    /**
     * 미리보기 모달 오픈
     */
    handlePreview(webpart?: webpartv3.Webpart) {
        const modal: appbase.IModalOpenable = this.$refs["modal"] as any;
        const options: appbase.TModalInfo<any> = {
            component: WebpartPreviewModal,
            props: {
                // 모달에서 정보가 변경될 것이기 때문에
                // webpart 참조를 넘기는 것보다 clone해서 전달
                webpart: webpart,
                title: this.$t("미리보기").toString(),
            },
        };
        modal.openModal(options);
    }

    /**
     * 웹파트를 삭제합니다.
     */
    async handleDelete(webpart: webpartv3.Webpart): Promise<boolean> {
        const message = this.$t("정말로_삭제하시겠습니까").toString();
        if (!(await (window as any).confirmAsync(message))) return false;
        if (!webpart.id) throw new Error("webpart.id is empty.");

        try {
            const succeed = await api.webpart.delete(webpart.id);
            if (succeed) {
                await (window as any).alertAsync(this.$t("M_Common_Deleted").toString());

                // 웹파트 목록 새로고침
                await this.refresh();
                return true;
            }
        } catch (err) {
            console.error(err);
        }
        await (window as any).alertAsync(this.$t("삭제에_실패하였습니다").toString());
        return false;
    }
}
